import { useEffect, useState } from 'react';
import { useAuth } from '@/shared/auth/AuthContext';
import { isAdminUser } from '@/shared/firebase/admin';
import { useNavigate } from 'react-router-dom';
import { MyResidenceTab } from './MyResidenceTab';
import { ResidentsTab } from './ResidentsTab';
import { DevicesTab } from './DevicesTab';
import { AccessCodesTab } from './AccessCodesTab';
import { OperationalTab } from './OperationalTab';
import { UsersTab } from './UsersTab';

type Tab = 'operational' | 'residence' | 'residents' | 'devices' | 'codes' | 'users';

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState<Tab>('operational');
  const [adminCheck, setAdminCheck] = useState<'pending'|'ok'|'denied'>('pending');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ok = await isAdminUser(user);
      if (!cancelled) setAdminCheck(ok ? 'ok' : 'denied');
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (adminCheck === 'pending') {
    return <div className="min-h-full grid place-items-center"><div className="text-slate-500">Comprobando permisos…</div></div>;
  }
  if (adminCheck === 'denied') {
    return (
      <div className="min-h-full grid place-items-center px-4">
        <div className="card max-w-md">
          <h1 className="text-lg font-extrabold text-danger">Acceso denegado</h1>
          <p className="text-sm text-slate-600 mt-2">
            Tu cuenta <b>{user?.email || 'desconocida'}</b> no tiene permisos de administrador para esta aplicación.
          </p>
          <div className="mt-4 flex gap-2">
            <button onClick={() => nav('/')} className="btn-ghost flex-1">Volver</button>
            <button onClick={logout} className="btn-primary flex-1">Cambiar usuario</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full flex flex-col">
      <header className="bg-white border-b border-slate-100 px-4 md:px-6 h-14 flex items-center gap-3 shrink-0">
        <div className="w-9 h-9 rounded-xl bg-slate-900 text-white grid place-items-center font-extrabold text-sm">A</div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-900 truncate">Administración</p>
          <p className="text-xs text-slate-500 truncate">{user?.email}</p>
        </div>
        <button onClick={logout} className="btn-ghost !py-1.5 !px-3 text-xs">Salir</button>
      </header>

      <nav className="bg-white border-b border-slate-100 px-4 flex gap-1 overflow-x-auto shrink-0">
        <TabBtn active={tab==='operational'} onClick={()=>setTab('operational')}>Vista cuidador</TabBtn>
        <TabBtn active={tab==='residents'}   onClick={()=>setTab('residents')}>Residentes</TabBtn>
        <TabBtn active={tab==='codes'}       onClick={()=>setTab('codes')}>Códigos</TabBtn>
        <TabBtn active={tab==='devices'}     onClick={()=>setTab('devices')}>Dispositivos</TabBtn>
        <TabBtn active={tab==='residence'}   onClick={()=>setTab('residence')}>Mi residencia</TabBtn>
        <TabBtn active={tab==='users'}       onClick={()=>setTab('users')}>👥 Usuarios</TabBtn>
      </nav>

      <main className="flex-1 overflow-auto p-4 md:p-6">
        {tab === 'operational' && <OperationalTab />}
        {tab === 'residence'   && <MyResidenceTab />}
        {tab === 'residents'   && <ResidentsTab />}
        {tab === 'devices'     && <DevicesTab />}
        {tab === 'codes'       && <AccessCodesTab />}
        {tab === 'users'       && <UsersTab />}
      </main>
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2.5 text-sm font-semibold whitespace-nowrap transition border-b-2
        ${active ? 'text-brand border-brand' : 'text-slate-500 border-transparent hover:text-slate-700'}`}
    >{children}</button>
  );
}
